import { useRef, useId } from 'react';

import { flexRender } from '@tanstack/react-table';
import { useVirtualizer } from '@tanstack/react-virtual';
import clsx from 'clsx';

import { NoResults, SkeletonRow } from '@/components';
import { DEFAULT_PAGE_SIZE } from '@/constants';

import { VirtualizedTableProps } from './types';

import styles from './styles.module.scss';

import ArrowUpIcon from '@/assets/icons/arrow.svg?react';

export const VirtualizedTable = <TData,>({
  table,
  filtersApplied,
  isLoading,
  skeletonRowCount = DEFAULT_PAGE_SIZE,
  height = 500,
  estimateSize = 49,
}: VirtualizedTableProps<TData>) => {
  const parentRef = useRef<HTMLDivElement>(null);
  const id = useId();
  const rows = table.getRowModel().rows;

  const virtualizer = useVirtualizer({
    count: isLoading ? skeletonRowCount : rows.length,
    getScrollElement: () => parentRef.current,
    estimateSize: () => estimateSize,
    overscan: 5,
  });

  const virtualItems = virtualizer.getVirtualItems();
  const columnWidths = table.getAllColumns().map((column) => column.getSize());

  return (
    <div className={styles.tableWrapper}>
      <table className={styles.table}>
        <thead className={styles.tableHeader}>
          {table.getHeaderGroups().map((headerGroup) => (
            <tr className={styles.tableRow} key={headerGroup.id}>
              {headerGroup.headers.map((header) => {
                const isSorted = header.column.getIsSorted();

                return (
                  <th
                    className={styles.tableCell}
                    key={header.id}
                    colSpan={header.colSpan}
                    style={{
                      width: header.column.getSize(),
                    }}
                  >
                    {!header.isPlaceholder && (
                      <div
                        className={clsx(styles.tableHeaderCellContent, header.column.getCanSort() && styles.sortable)}
                        onClick={header.column.getToggleSortingHandler()}
                      >
                        {flexRender(header.column.columnDef.header, header.getContext())}
                        {header.column.getCanSort() && !isLoading && (
                          <div className={clsx(styles.sortIconWrapper, isSorted && styles.sorted)}>
                            <ArrowUpIcon
                              className={clsx(styles.sortIcon, {
                                [styles.desc]: isSorted === 'desc',
                                [styles.asc]: isSorted === 'asc',
                              })}
                              width={16}
                              height={16}
                            />
                          </div>
                        )}
                      </div>
                    )}
                  </th>
                );
              })}
            </tr>
          ))}
        </thead>
      </table>

      <div
        className={styles.virtualizedTableBody}
        ref={parentRef}
        style={{
          height: `${height}px`,
        }}
      >
        {isLoading ? (
          <div
            className={styles.virtualizedContainer}
            style={{
              height: `${virtualizer.getTotalSize()}px`,
            }}
          >
            {virtualItems.map((virtualItem) => (
              <div
                key={virtualItem.key}
                className={styles.virtualizedItem}
                style={{
                  height: `${virtualItem.size}px`,
                  transform: `translateY(${virtualItem.start}px)`,
                }}
              >
                <table className={styles.table}>
                  <tbody>
                    <SkeletonRow
                      key={`skeleton-item-${id}-${virtualItem.index}`}
                      columnCount={table.getAllColumns().length}
                      columnWidths={columnWidths}
                    />
                  </tbody>
                </table>
              </div>
            ))}
          </div>
        ) : filtersApplied && rows.length < 1 ? (
          <table className={styles.table}>
            <tbody className={styles.tableBody}>
              <tr>
                <td colSpan={table.getAllColumns().length}>
                  <div className={styles.noResults}>
                    <NoResults />
                  </div>
                </td>
              </tr>
            </tbody>
          </table>
        ) : (
          <div
            className={styles.virtualizedContainer}
            style={{
              height: `${virtualizer.getTotalSize()}px`,
            }}
          >
            {virtualItems.map((virtualItem) => {
              const row = rows[virtualItem.index];
              return (
                <div
                  key={row.id}
                  className={styles.virtualizedItem}
                  style={{
                    height: `${virtualItem.size}px`,
                    transform: `translateY(${virtualItem.start}px)`,
                  }}
                >
                  <table className={styles.table}>
                    <tbody>
                      <tr className={styles.tableRow}>
                        {row.getVisibleCells().map((cell) => (
                          <td
                            className={styles.tableCell}
                            key={cell.id}
                            {...{
                              style: {
                                width: cell.column.getSize(),
                              },
                            }}
                          >
                            {flexRender(cell.column.columnDef.cell, cell.getContext())}
                          </td>
                        ))}
                      </tr>
                    </tbody>
                  </table>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
};
